import * as React from 'react';
import { Pressable, View } from 'react-native';
import { cn } from '@/lib/utils';
import { Text } from '@/components/ui/text';
import type { TourStep, TourStepAction } from '../types';

interface TourTooltipCardProps {
  step: TourStep;
  currentStep: number;
  totalSteps: number;
  onAction: (action: TourStepAction) => void;
  testID?: string;
}

function TourTooltipCard({
  step,
  currentStep,
  totalSteps,
  onAction,
  testID,
}: TourTooltipCardProps) {
  return (
    <View
      testID={testID ?? 'tour-tooltip-card'}
      className="bg-card border border-border rounded-xl p-4"
      style={{ overflow: 'hidden' }}
    >
      <Text className="text-card-foreground text-base font-semibold mb-1">
        {step.title}
      </Text>
      <Text className="text-muted-foreground text-sm mb-4">
        {step.body}
      </Text>

      <View className="flex-row items-center justify-center gap-1.5 mb-4">
        {Array.from({ length: totalSteps }, (_, i) => (
          <View
            key={i}
            testID={`tooltip-dot-${i + 1}`}
            accessibilityLabel={
              i === currentStep
                ? `Step ${i + 1} of ${totalSteps} (current)`
                : `Step ${i + 1} of ${totalSteps}`
            }
            className={cn(
              'w-1.5 h-1.5 rounded-full',
              i === currentStep ? 'bg-primary' : 'bg-muted-foreground/30',
            )}
          />
        ))}
      </View>

      <View className="flex-row justify-end gap-3">
        {step.secondaryAction && (
          <Pressable
            onPress={() => onAction(step.secondaryAction!)}
            accessibilityLabel={step.secondaryAction.label}
            accessibilityRole="button"
            className="rounded-lg py-2.5 px-4 items-center"
          >
            <Text className="text-muted-foreground font-medium text-sm">
              {step.secondaryAction.label}
            </Text>
          </Pressable>
        )}
        <Pressable
          onPress={() => onAction(step.primaryAction)}
          accessibilityLabel={step.primaryAction.label}
          accessibilityRole="button"
          className="bg-primary rounded-lg py-2.5 px-5 items-center"
        >
          <Text className="text-primary-foreground font-semibold text-sm">
            {step.primaryAction.label}
          </Text>
        </Pressable>
      </View>
    </View>
  );
}

export { TourTooltipCard };
export type { TourTooltipCardProps };
